/**
 * `useDpr` — live subscription to one `dpr/{projectId}_{date}` doc.
 *
 * Web SDK port of
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/dpr/useDpr.ts
 *
 * Mobile + web read the same Firestore document; no schema drift.
 *
 * Returns `data: null` when the doc doesn't exist yet (a brand-new
 * day with no report saved). The form binds to local state seeded
 * from `data` (or empty), so absence of a doc is a normal "create
 * mode" state, not an error.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';

import { dprDocId, type DailyProgressReport } from '@/lib/dpr';

export type UseDprResult = {
  data: DailyProgressReport | null;
  loading: boolean;
};

export function useDpr(
  projectId: string | undefined,
  date: string | undefined,
): UseDprResult {
  const [data, setData] = useState<DailyProgressReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !date) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      doc(db, 'dpr', dprDocId(projectId, date)),
      (snap) => {
        if (!snap.exists()) {
          setData(null);
          setLoading(false);
          return;
        }
        setData({
          id: snap.id,
          ...(snap.data() as Omit<DailyProgressReport, 'id'>),
        });
        setLoading(false);
      },
      (err) => {
        console.warn('[useDpr] snapshot error:', err);
        setData(null);
        setLoading(false);
      },
    );
  }, [projectId, date]);

  return { data, loading };
}
